/**
 * Request/response schemas for the dermatology specialty module (`BACKLOG.md` M3,
 * `docs/comparison-emrx.md` §5.2/§5.3). Same reasoning as `clinicalRequests.ts`: these live in
 * `shared/` so the server validates against the exact schema the web app infers its types from, every
 * object schema is strict so an unrecognised key is a rejection rather than silently ignored, and no
 * server-owned value (an id, `orgId`, a timestamp, `recordedBy`) appears in any request schema — the
 * route layer supplies those from the session, never from the body.
 */

import { z } from 'zod';

import {
  calendarDateSchema,
  confidentialityReasonsInputSchema,
  restrictionReasonSchema,
} from './clinicalRequests.ts';
import { CLINICAL_ERROR_CODES } from './encounterRequests.ts';
import {
  FITZPATRICK_SKIN_TYPES,
  IPLEDGE_RISK_CATEGORIES,
} from './dermatology/patientAssessment.ts';
import { PATCH_READINGS, PATCH_READ_DAYS } from './dermatology/patchTest.ts';

export const MAX_ALLERGEN_LENGTH = 200;
export const MAX_SERIES_NAME_LENGTH = 100;
export const MAX_BODY_SITE_LENGTH = 100;
export const MAX_ICD10_LENGTH = 20;

const trimmed = (max: number) => z.string().trim().min(1).max(max);

// --- Guardrail profile (skin phototype, iPLEDGE risk category) ----------------------------------

/**
 * Both guardrail fields optional so a caller can record one without the other — a patient may
 * answer the phototype question at intake and the iPLEDGE question only later, if isotretinoin ever
 * comes up. At least one of the two must be present; an empty patch is rejected rather than silently
 * accepted as a no-op, matching the server's own `evaluateChartAccess` write-audit expectations for
 * a real write.
 *
 * `confidentialityReasons` is genuinely optional (`.optional()`, not `confidentialityReasonsInputSchema`'s
 * own `.default([])`) for the identical reason the two guardrail fields are: omitted means "leave the
 * label exactly as it is," never "clear it." A caller answering only the phototype question must not
 * silently strip an iPLEDGE-driven restriction someone else set last month — `confidentialityReasonsInputSchema`'s
 * default-to-`[]` behavior is correct for every *create* schema in this module (`createPatchTestPanelRequestSchema`,
 * `createDestructionProcedureRequestSchema`), where there is no existing label to preserve, but wrong
 * here. Added in review 2026-08-18: the field carrying the iPLEDGE reproductive-risk-category answer
 * — genuinely sensitive — previously had no way to be confidentiality-protected at all.
 */
export const updateDermatologyProfileRequestSchema = z
  .object({
    fitzpatrickSkinType: z.enum(FITZPATRICK_SKIN_TYPES).optional(),
    ipledgeRiskCategory: z.enum(IPLEDGE_RISK_CATEGORIES).optional(),
    confidentialityReasons: z.array(restrictionReasonSchema).optional(),
  })
  .strict()
  .refine(
    (value) => value.fitzpatrickSkinType !== undefined || value.ipledgeRiskCategory !== undefined,
    { message: 'at least one of fitzpatrickSkinType or ipledgeRiskCategory is required' },
  );
export type UpdateDermatologyProfileRequest = z.infer<typeof updateDermatologyProfileRequestSchema>;

export const dermatologyProfileResponseSchema = z.object({
  patientId: z.uuid(),
  // Response, not request — plain string, matching `clinicalRequests.ts`'s own split
  // (`vitalResponseSchema.vitalType`, e.g.): the narrow enum guards what a caller may *write*, and a
  // value already stored is read back as-is, with no cast needed at the service→response boundary
  // (found in review 2026-08-18 as one of four unchecked casts this split now removes).
  fitzpatrickSkinType: z.string().nullable(),
  // Plain string, not `z.iso.datetime()` — this is a read-back of the DB's own timestamptz text
  // format (Postgres's own wire format, not strict ISO 8601: a space separator and `+00` rather
  // than `+00:00`), matching `clinicalRequests.ts`'s own `vitalResponseSchema.recordedAt` split
  // between a strict *request* validator and a lenient *response* readback. Using the strict
  // validator here broke every later load of a patient whose guardrail fields had ever been
  // recorded (found 2026-08-18, while building the demo walkthrough).
  fitzpatrickRecordedAt: z.string().nullable(),
  ipledgeRiskCategory: z.string().nullable(),
  ipledgeRecordedAt: z.string().nullable(),
  confidentialityReasons: z.array(z.string()),
});
export type DermatologyProfileResponse = z.infer<typeof dermatologyProfileResponseSchema>;

/** The envelope `GET`/`PATCH .../dermatology/profile` actually return. */
export const dermatologyProfileEnvelopeSchema = z.object({
  profile: dermatologyProfileResponseSchema,
});

// --- Patch testing --------------------------------------------------------------------------------

export const createPatchTestPanelRequestSchema = z
  .object({
    seriesName: trimmed(MAX_SERIES_NAME_LENGTH),
    appliedOn: calendarDateSchema,
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreatePatchTestPanelRequest = z.infer<typeof createPatchTestPanelRequestSchema>;

export const patchReadDaySchema = z.union([
  z.literal(PATCH_READ_DAYS[0]),
  z.literal(PATCH_READ_DAYS[1]),
  z.literal(PATCH_READ_DAYS[2]),
]);

export const addPatchTestReadingRequestSchema = z
  .object({
    allergen: trimmed(MAX_ALLERGEN_LENGTH),
    readingDay: patchReadDaySchema,
    reading: z.enum(PATCH_READINGS),
    readOn: calendarDateSchema,
  })
  .strict();
export type AddPatchTestReadingRequest = z.infer<typeof addPatchTestReadingRequestSchema>;

export const patchTestReadingResponseSchema = z.object({
  id: z.uuid(),
  allergen: z.string(),
  // Response, not request — plain types, same split `dermatologyProfileResponseSchema` above uses:
  // the narrow request-side unions (`patchReadDaySchema`, `z.enum(PATCH_READINGS)`) guard what a
  // caller may write; a value already stored is read back as-is, with no cast needed at the
  // service→response boundary.
  readingDay: z.number().int(),
  reading: z.string(),
  readOn: calendarDateSchema,
});
export type PatchTestReadingResponse = z.infer<typeof patchTestReadingResponseSchema>;

/**
 * One allergen's guarded result — `@emr/shared`'s `reportableReading`/`isReportablePositive`
 * (`dermatology/patchTest.ts`) applied to that allergen's readings across all three days, computed
 * server-side so every consumer (this schema's own `positives`, the UI) sees the day-2-guarded
 * answer rather than re-deriving it from raw per-day readings and risking the exact misreading the
 * guard exists to prevent. `reportableReading` is included even for a negative allergen — `'NT'`
 * when nothing beyond day 2 has been read yet — so a caller can distinguish "guarded negative" from
 * "not yet reportable" without re-running the guard itself.
 */
export const patchTestAllergenResultSchema = z.object({
  allergen: z.string(),
  reportableReading: z.enum(PATCH_READINGS),
  isReportablePositive: z.boolean(),
});
export type PatchTestAllergenResult = z.infer<typeof patchTestAllergenResultSchema>;

export const patchTestPanelResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  seriesName: z.string(),
  appliedOn: calendarDateSchema,
  // Response, not request — plain string, matching `clinicalRequests.ts`'s own split: the narrow
  // enum guards what a caller may *write*, and a value already stored is read back as-is.
  confidentialityReasons: z.array(z.string()),
  readings: z.array(patchTestReadingResponseSchema),
  /** One entry per allergen with at least one reading, day-2-guarded. See the schema above. */
  allergenResults: z.array(patchTestAllergenResultSchema),
});
export type PatchTestPanelResponse = z.infer<typeof patchTestPanelResponseSchema>;

/** The envelope `POST .../patch-tests` returns. */
export const patchTestPanelEnvelopeSchema = z.object({ panel: patchTestPanelResponseSchema });
/** The envelope `GET .../patch-tests` returns. */
export const patchTestPanelListEnvelopeSchema = z.object({
  panels: z.array(patchTestPanelResponseSchema),
});
/** The envelope `POST .../patch-tests/:panelId/readings` returns. */
export const patchTestReadingEnvelopeSchema = z.object({
  reading: patchTestReadingResponseSchema,
});

// --- Destruction procedures -----------------------------------------------------------------------

export const destructionCptLineResponseSchema = z.object({
  cpt: z.enum(['17000', '17003', '17004']),
  display: z.string(),
  units: z.number().int().positive(),
});

export const createDestructionProcedureRequestSchema = z
  .object({
    /** `null`/omitted is the "not documented" case `destructionCptCodes` refuses on, on purpose. */
    lesionCount: z.number().int().positive().nullable().default(null),
    bodySite: trimmed(MAX_BODY_SITE_LENGTH).optional(),
    performedOn: calendarDateSchema,
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreateDestructionProcedureRequest = z.infer<
  typeof createDestructionProcedureRequestSchema
>;

export const destructionProcedureResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  lesionCount: z.number().int().positive().nullable(),
  bodySite: z.string().nullable(),
  performedOn: calendarDateSchema,
  // Response, not request — plain string, matching `clinicalRequests.ts`'s own split: the narrow
  // enum guards what a caller may *write*, and a value already stored is read back as-is.
  confidentialityReasons: z.array(z.string()),
  // Computed at read time from `lesionCount` via `destructionCptCodes` — never stored, so it cannot
  // go stale if the banding rule changes. `codingRefused` mirrors `DestructionCptResult`'s `ok: false`
  // case: `lines` is empty and the caller must not treat that as "zero lesions were coded."
  cptLines: z.array(destructionCptLineResponseSchema),
  codingRefused: z.boolean(),
});
export type DestructionProcedureResponse = z.infer<typeof destructionProcedureResponseSchema>;

/** The envelope `POST .../destruction-procedures` returns. */
export const destructionProcedureEnvelopeSchema = z.object({
  procedure: destructionProcedureResponseSchema,
});
/** The envelope `GET .../destruction-procedures` returns. */
export const destructionProcedureListEnvelopeSchema = z.object({
  procedures: z.array(destructionProcedureResponseSchema),
});

// --- Recall / surveillance -------------------------------------------------------------------------

export const recordSkinExamRequestSchema = z
  .object({
    icd10Code: trimmed(MAX_ICD10_LENGTH),
    examOn: calendarDateSchema,
  })
  .strict();
export type RecordSkinExamRequest = z.infer<typeof recordSkinExamRequestSchema>;

export const recallStatusResponseSchema = z.object({
  conditionId: z.uuid(),
  icd10: z.string(),
  kind: z.enum(['surveillance', 'disease_control']),
  reason: z.string(),
  months: z.number().int().positive(),
  lastDischargedOn: calendarDateSchema.nullable(),
  dueDate: calendarDateSchema.nullable(),
  state: z.enum(['overdue', 'due_soon', 'scheduled_later']),
  daysUntilDue: z.number().int().nullable(),
});
export type RecallStatusResponse = z.infer<typeof recallStatusResponseSchema>;

/** The envelope `GET .../recall` returns. */
export const recallStatusListEnvelopeSchema = z.object({
  statuses: z.array(recallStatusResponseSchema),
});

/** The envelope `POST .../recall/skin-exams` returns. */
export const skinExamEnvelopeSchema = z.object({
  exam: z.object({ id: z.uuid(), icd10Code: z.string(), examOn: calendarDateSchema }),
});

// --- Failure vocabulary -----------------------------------------------------------------------

/**
 * Closed union so the web app can branch on a code without matching on prose. Extends
 * `encounterRequests.ts`'s `CLINICAL_ERROR_CODES` rather than re-declaring the codes this module
 * shares with it — the same pattern `clinicalRequests.ts`'s own `PATIENT_ERROR_CODES` already uses
 * (`[...CLINICAL_ERROR_CODES, 'mrn_taken']`), reused here rather than re-derived. `not_entitled` is
 * the one code genuinely new to this module, and it is distinct from `forbidden`: see
 * `server/src/auth/middleware.ts`'s `requireFeature` for why the two must never be conflated.
 */
export const DERMATOLOGY_ERROR_CODES = [...CLINICAL_ERROR_CODES, 'not_entitled'] as const;
export type DermatologyErrorCode = (typeof DERMATOLOGY_ERROR_CODES)[number];
